(() => {
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initDrawers = (root = document) => {
    root.querySelectorAll('[data-ps-drawer-open]').forEach((button) => {
      if (button.dataset.psBound) return;
      button.dataset.psBound = 'true';
      const drawer = document.getElementById(button.getAttribute('aria-controls'));
      if (!drawer) return;

      button.addEventListener('click', () => {
        drawer.showModal();
        button.setAttribute('aria-expanded', 'true');
      });

      drawer.addEventListener('close', () => {
        button.setAttribute('aria-expanded', 'false');
        button.focus();
      });

      drawer.addEventListener('click', (event) => {
        if (event.target === drawer || event.target.closest('[data-ps-drawer-close]')) drawer.close();
      });
    });
  };

  const ease = {
    linear: (t) => t,
    outCubic: (t) => 1 - Math.pow(1 - t, 3),
    outExpo: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
    outCirc: (t) => Math.sqrt(1 - Math.pow(t - 1, 2)),
    outBack: (t) => {
      const c1 = 1.2;
      const c3 = c1 + 1;
      return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    },
  };

  class Tweens {
    constructor(onFrame) {
      this.items = new Set();
      this.onFrame = onFrame;
      this.frame = null;
    }

    to(target, prop, to, { duration = 500, delay = 0, easing = ease.outCubic, from } = {}) {
      this.items.forEach((item) => {
        if (item.target === target && item.prop === prop) {
          this.items.delete(item);
          item.resolve(false);
        }
      });
      if (from !== undefined) target[prop] = from;

      return new Promise((resolve) => {
        this.items.add({
          target,
          prop,
          from: target[prop],
          to,
          duration,
          delay,
          easing,
          start: performance.now(),
          resolve,
        });
        this.run();
      });
    }

    run() {
      if (this.frame) return;
      const tick = () => {
        const now = performance.now();
        this.items.forEach((item) => {
          const elapsed = now - item.start - item.delay;
          if (elapsed < 0) return;
          const progress = item.duration > 0 ? Math.min(1, elapsed / item.duration) : 1;
          item.target[item.prop] = item.from + (item.to - item.from) * item.easing(progress);
          if (progress === 1) {
            this.items.delete(item);
            item.resolve(true);
          }
        });
        this.onFrame();
        this.frame = this.items.size ? requestAnimationFrame(tick) : null;
      };
      this.frame = requestAnimationFrame(tick);
    }
  }

  const MOTION_PACE = 1.3;
  const AUTOPLAY_IDLE = 25000;
  const AUTOPLAY_INTERVAL = 5000;

  class PsCatalogExplorer extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.stage = this.querySelector('[data-ps-stage]');
      this.cards = Array.from(this.querySelectorAll('[data-ps-card]')).map((element, index) => ({
        element,
        slot: index,
        offsetY: 0,
        scale: 1,
        opacity: 0,
        exitedFront: false,
        exitedBack: false,
      }));
      this.count = this.cards.length;
      if (!this.count || !this.stage) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.motion = { position: 0 };
      this.target = 0;
      this.tweens = new Tweens(() => this.render());

      this.readMetrics();
      this.onResize = () => {
        this.readMetrics();
        this.render();
      };
      window.addEventListener('resize', this.onResize);

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          this.scheduleAutoplay(AUTOPLAY_IDLE);
          this.goToIndex(index);
        });
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });

      this.cards.forEach((card) => {
        card.element.addEventListener(
          'click',
          (event) => {
            this.scheduleAutoplay(AUTOPLAY_IDLE);
            if (card.slot === this.target) return;
            event.preventDefault();
            event.stopPropagation();
            this.goToSlot(card.slot);
          },
          true
        );
      });

      this.classList.add('is-ready');
      this.render();
      this.syncState();
      this.observeIntro();
      this.observeVisibility();
      this.scheduleAutoplay(AUTOPLAY_IDLE);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      this.observer?.disconnect();
      this.visibility?.disconnect();
      clearTimeout(this.autoplayTimer);
    }

    observeVisibility() {
      this.inView = !('IntersectionObserver' in window);
      if (this.inView) return;
      this.visibility = new IntersectionObserver((entries) => {
        this.inView = entries[entries.length - 1].isIntersecting;
      }, { threshold: 0.3 });
      this.visibility.observe(this.stage);
    }

    scheduleAutoplay(delay) {
      clearTimeout(this.autoplayTimer);
      if (this.reducedMotion || this.count < 2) return;
      this.autoplayTimer = setTimeout(() => {
        if (this.inView && !document.hidden) this.goToSlot(this.target + 1);
        this.scheduleAutoplay(AUTOPLAY_INTERVAL);
      }, delay);
    }

    readMetrics() {
      const styles = getComputedStyle(this.stage);
      this.stepY = parseFloat(styles.getPropertyValue('--ps-stack-y')) || -70;
      this.stepZ = parseFloat(styles.getPropertyValue('--ps-stack-z')) || -70;
      this.visibleDepth = parseFloat(styles.getPropertyValue('--ps-stack-visible')) || 3;
    }

    observeIntro() {
      if (this.reducedMotion || !('IntersectionObserver' in window)) {
        this.cards.forEach((card) => (card.opacity = 1));
        this.render();
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.cards.forEach((card, index) => {
            const delay = index * 75;
            this.tweens.to(card, 'offsetY', 0, { from: 80, duration: 1100, delay, easing: ease.outExpo });
            this.tweens.to(card, 'scale', 1, { from: 0.94, duration: 1100, delay, easing: ease.outExpo });
            this.tweens.to(card, 'opacity', 1, { from: 0, duration: 400, delay });
          });
        },
        { threshold: 0.3 }
      );
      this.observer.observe(this.stage);
    }

    onKeydown(event, index) {
      const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
      let next;
      if (event.key in keys) next = (index + keys[event.key] + this.count) % this.count;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.count - 1;
      else return;
      event.preventDefault();
      this.scheduleAutoplay(AUTOPLAY_IDLE);
      this.goToIndex(next);
      this.tabs[next].focus();
    }

    get activeIndex() {
      return ((this.target % this.count) + this.count) % this.count;
    }

    goToIndex(index) {
      this.goToSlot(this.target + index - this.activeIndex);
    }

    goToSlot(slot) {
      if (slot === this.target) return;
      this.target = slot;
      this.syncState();
      this.tweens.to(this.motion, 'position', slot, {
        duration: this.ms(700),
        easing: ease.outCirc,
      });
    }

    syncState() {
      const active = this.activeIndex;
      this.tabs.forEach((tab, index) => {
        const selected = index === active;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.cards.forEach((card, index) => {
        const isFront = index === active;
        card.element.toggleAttribute('data-front', isFront);
        card.element.setAttribute('aria-hidden', String(!isFront));
        card.element.querySelectorAll('a, button').forEach((control) => {
          control.tabIndex = isFront ? 0 : -1;
        });
      });

      const list = this.tabs[active]?.closest('[role="tablist"]');
      if (list && list.scrollWidth > list.clientWidth) {
        const tab = this.tabs[active];
        list.scrollTo({ left: tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth' });
      }
    }

    ms(duration) {
      return this.reducedMotion ? 0 : duration * MOTION_PACE;
    }

    updateCycle(card, depth) {
      const margin = 0.075;
      const last = this.count - 1;

      if (depth < -margin && !card.exitedFront) {
        card.exitedFront = true;
        this.tweens.to(card, 'offsetY', 70, { duration: this.ms(500) });
        this.tweens.to(card, 'scale', 0.94, { duration: this.ms(500) });
        this.tweens.to(card, 'opacity', 0, { duration: this.ms(220), delay: this.ms(40) }).then((done) => {
          if (!done || !card.exitedFront) return;
          card.exitedFront = false;
          card.exitedBack = true;
          card.slot += this.count;
          this.render();
        });
      } else if (depth > last + margin && !card.exitedBack) {
        card.exitedBack = true;
        this.tweens.to(card, 'offsetY', -50, { duration: this.ms(500) });
        this.tweens.to(card, 'scale', 0.94, { duration: this.ms(500) });
        this.tweens.to(card, 'opacity', 0, { duration: this.ms(250), delay: this.ms(200) }).then((done) => {
          if (!done || !card.exitedBack) return;
          card.exitedBack = false;
          card.exitedFront = true;
          card.slot -= this.count;
          this.render();
        });
      } else if (depth >= -margin && depth <= last + margin && (card.exitedFront || card.exitedBack)) {
        const from = card.exitedFront ? 60 : -40;
        card.exitedFront = false;
        card.exitedBack = false;
        this.tweens.to(card, 'offsetY', 0, { from, duration: this.ms(1000), easing: ease.outExpo });
        this.tweens.to(card, 'scale', 1, { from: 0.94, duration: this.ms(1000), easing: ease.outExpo });
        this.tweens.to(card, 'opacity', 1, { duration: this.ms(400) });
      }
    }

    render() {
      const position = this.motion.position;
      this.cards.forEach((card) => {
        const depth = card.slot - position;
        this.updateCycle(card, depth);

        const fade = Math.max(0, Math.min(1, this.visibleDepth + 1 - depth));
        const opacity = card.opacity * fade;
        const style = card.element.style;
        style.transform = `translate3d(0, ${depth * this.stepY + card.offsetY}px, ${depth * this.stepZ}px) scale(${card.scale})`;
        style.opacity = opacity.toFixed(3);
        style.zIndex = String(this.count - Math.round(depth));
        style.pointerEvents = opacity > 0.01 ? 'auto' : 'none';
      });
    }
  }

  if (!customElements.get('ps-catalog-explorer')) {
    customElements.define('ps-catalog-explorer', PsCatalogExplorer);
  }

  class PsCollection extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('[data-ps-filter-form]');
      form?.addEventListener('change', () => {
        const params = new URLSearchParams(new FormData(form));
        window.location.assign(`${form.action}?${params}#${this.closest('section').id}`);
      });

      const sort = this.querySelector('[data-ps-sort]');
      if (sort) {
        const label = sort.parentElement.querySelector('.ps-sort__label');
        sort.parentElement.style.setProperty('--ps-sort-label', `${label.offsetWidth}px`);
        sort.addEventListener('change', () => {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', sort.value);
          url.searchParams.delete('page');
          url.hash = this.closest('section').id;
          window.location.assign(url);
        });
      }

      const filters = this.querySelector('[data-ps-filters]');
      if (filters) {
        const desktop = window.matchMedia('(min-width: 990px)');
        const hasActive = !!filters.querySelector('input:checked, [aria-current]');
        const sync = () => {
          filters.open = desktop.matches || hasActive;
        };
        sync();
        desktop.addEventListener('change', sync);
      }
    }
  }

  if (!customElements.get('ps-collection')) {
    customElements.define('ps-collection', PsCollection);
  }

  class PsGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ps-track]');
      this.slides = [...this.track.children];
      this.thumbs = [...this.querySelectorAll('[data-ps-thumb]')];
      this.thumbList = this.querySelector('[data-ps-thumbs]');
      this.counter = this.querySelector('[data-ps-current]');
      this.current = -1;
      this.target = null;

      this.querySelector('[data-ps-prev]')?.addEventListener('click', () => this.go(this.current - 1));
      this.querySelector('[data-ps-next]')?.addEventListener('click', () => this.go(this.current + 1));
      this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.go(index)));

      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.go(this.current - 1);
        else if (event.key === 'ArrowRight') this.go(this.current + 1);
        else return;
        event.preventDefault();
      });

      let frame = 0;
      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => this.sync());
        },
        { passive: true }
      );
      this.track.addEventListener('scrollend', () => {
        this.target = null;
        this.sync();
      });

      this.setupLightbox();
      this.go(Number(this.dataset.start) || 0, false);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const id = data?.variant?.featured_media?.id;
          const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(id));
          if (index >= 0) this.go(index);
        });
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    go(index, smooth = true) {
      const count = this.slides.length;
      if (!count) return;
      const next = (index + count) % count;
      this.target = next;
      clearTimeout(this.targetTimer);
      this.targetTimer = setTimeout(() => (this.target = null), 900);
      this.track.scrollTo({
        left: next * this.track.clientWidth,
        behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto',
      });
      this.setActive(next);
    }

    sync() {
      if (this.target !== null) return;
      const index = Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
      if (index !== this.current) this.setActive(index);
    }

    setActive(index) {
      if (index === this.current) return;
      this.current = index;
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.toggleAttribute('aria-hidden', !active);
        slide.querySelectorAll('button, a, video, iframe').forEach((el) => {
          el.tabIndex = active ? 0 : -1;
        });
        if (!active) slide.querySelector('video')?.pause();
      });
      this.thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = index + 1;

      const thumb = this.thumbs[index];
      if (thumb && this.thumbList) {
        const item = thumb.parentElement;
        const list = this.thumbList;
        const left = item.offsetLeft - (list.clientWidth - item.offsetWidth) / 2;
        list.scrollTo({ left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    }

    setupLightbox() {
      const dialog = this.querySelector('[data-ps-lightbox]');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      const img = dialog.querySelector('[data-ps-lightbox-img]');
      const counter = dialog.querySelector('[data-ps-lightbox-counter]');
      const zooms = [...this.querySelectorAll('[data-ps-zoom]')];
      let position = 0;

      const show = (i) => {
        position = (i + zooms.length) % zooms.length;
        const zoom = zooms[position];
        img.src = zoom.dataset.full;
        img.alt = zoom.querySelector('img')?.alt || '';
        if (counter) counter.textContent = `${position + 1} / ${zooms.length}`;
        this.go(this.slides.indexOf(zoom.closest('.ps-gallery__slide')), false);
      };

      zooms.forEach((zoom, i) =>
        zoom.addEventListener('click', () => {
          show(i);
          dialog.showModal();
          document.documentElement.classList.add('ps-lightbox-open');
        })
      );

      const multiple = zooms.length > 1;
      dialog.querySelectorAll('[data-ps-lightbox-prev], [data-ps-lightbox-next]').forEach((button) => {
        button.hidden = !multiple;
      });
      if (counter) counter.hidden = !multiple;

      dialog.querySelector('[data-ps-lightbox-prev]')?.addEventListener('click', () => show(position - 1));
      dialog.querySelector('[data-ps-lightbox-next]')?.addEventListener('click', () => show(position + 1));
      dialog.querySelector('[data-ps-lightbox-close]').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('keydown', (event) => {
        if (!multiple) return;
        if (event.key === 'ArrowLeft') show(position - 1);
        if (event.key === 'ArrowRight') show(position + 1);
      });
      dialog.addEventListener('close', () => document.documentElement.classList.remove('ps-lightbox-open'));

      let startX = null;
      dialog.addEventListener('touchstart', (event) => (startX = event.touches[0].clientX), { passive: true });
      dialog.addEventListener('touchend', (event) => {
        if (startX === null || !multiple) return;
        const delta = event.changedTouches[0].clientX - startX;
        if (Math.abs(delta) > 50) show(position + (delta < 0 ? 1 : -1));
        startX = null;
      });
    }
  }

  if (!customElements.get('ps-gallery')) {
    customElements.define('ps-gallery', PsGallery);
  }

  class PsCart extends HTMLElement {
    connectedCallback() {
      this.cartUrl = window.routes?.cart_url || '/cart';
      this.timers = new Map();

      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-ps-step]');
        if (step) {
          const input = step.parentElement.querySelector('[data-ps-qty]');
          const increment = Number(input.step) || 1;
          const max = input.max ? Number(input.max) : Infinity;
          const value = Math.min(max, Math.max(0, Number(input.value) + Number(step.dataset.psStep) * increment));
          if (value === Number(input.value)) return;
          input.value = value;
          this.queue(input, 350);
          return;
        }
        const remove = event.target.closest('[data-ps-remove]');
        if (remove) {
          event.preventDefault();
          const line = remove.closest('[data-ps-line]');
          line.classList.add('is-removing');
          this.change(line.dataset.psLine, 0);
        }
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-ps-qty]')) this.queue(event.target, 0);
        if (event.target.matches('[data-ps-note]')) {
          fetch(`${this.cartUrl}/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: event.target.value }),
          });
        }
      });
    }

    queue(input, delay) {
      const key = input.closest('[data-ps-line]').dataset.psLine;
      clearTimeout(this.timers.get(key));
      this.timers.set(
        key,
        setTimeout(() => this.change(key, Number(input.value)), delay)
      );
    }

    async change(key, quantity) {
      this.classList.add('is-loading');
      try {
        const response = await fetch(`${this.cartUrl}/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });
        const cart = await response.json();
        await this.refresh();
        if (typeof cart.item_count === 'number') updateCartCount(cart.item_count);
      } catch (error) {
        window.location.reload();
      } finally {
        this.classList.remove('is-loading');
      }
    }

    async refresh() {
      const html = await fetch(`${this.cartUrl}?section_id=${this.dataset.section}`).then((r) => r.text());
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('ps-cart');
      if (next) this.innerHTML = next.innerHTML;
    }
  }

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-ps-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  };

  if (!customElements.get('ps-cart')) {
    customElements.define('ps-cart', PsCart);
  }

  const initQuickAdd = () => {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-ps-quick-add]');
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('button[type="submit"]');
      const label = form.querySelector('[data-ps-quick-add-label]');
      if (button.classList.contains('is-loading')) return;
      label.dataset.original ??= label.textContent;
      const original = label.dataset.original;
      button.classList.add('is-loading');
      try {
        const response = await fetch(`${form.action}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error(response.status);
        const cart = await fetch(`${form.action.replace(/\/add$/, '')}.js`).then((r) => r.json());
        updateCartCount(cart.item_count);
        button.classList.add('is-added');
        label.textContent = button.dataset.added;
        document.querySelector('ps-cart')?.refresh();
      } catch (error) {
        label.textContent = button.dataset.error;
      } finally {
        button.classList.remove('is-loading');
        clearTimeout(button.psReset);
        button.psReset = setTimeout(() => {
          button.classList.remove('is-added');
          label.textContent = original;
        }, 2200);
      }
    });
  };

  const REVEAL_SELECTOR = [
    '.ps-hero',
    '.ps-reseller',
    '.ps-accent',
    '.ps-display',
    '.ps-title',
    '.ps-hero__content > :not(.ps-accent, .ps-display)',
    '.ps-explorer__intro .ps-lead',
    '.ps-explorer__tabs > li',
    '.ps-products__head .ps-link-arrow',
    '.ps-products__grid > li',
    '.ps-catalog__block',
    '.ps-catalog__tools',
    '.ps-catalog__products > li',
    '.ps-band__content > *',
    '.ps-band__button',
    '.ps-faq__item',
    '.ps-faq__help',
    '.ps-contact__intro > .ps-lead',
    '.ps-contact__media',
    '.ps-contact__topics > li',
    '.ps-contact__info > li',
    '.ps-contact__card',
    '.ps-footer__lead',
    '.ps-newsletter',
    '.ps-footer__col',
    '.ps-footer__brand',
    '.ps-footer__bottom',
  ].join(',');

  const splitLines = (heading) => {
    if (heading.classList.contains('is-split')) return;
    const lines = heading.innerHTML.split(/<br\s*\/?>/i).map((line) => line.trim()).filter(Boolean);
    heading.innerHTML = lines
      .map((line, index) => `<span class="ps-line"><span class="ps-line__inner" style="--ps-line: ${index}">${line}</span></span>`)
      .join('');
    heading.classList.add('is-split');
  };

  let revealObserver;
  const initReveal = (root = document) => {
    if (!document.documentElement.classList.contains('ps-motion')) return;
    root.querySelectorAll('.ps .ps-display, .ps .ps-title').forEach(splitLines);

    revealObserver ??= new IntersectionObserver(
      (entries) => {
        entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => entry.target)
          .sort((a, b) => {
            const ra = a.getBoundingClientRect();
            const rb = b.getBoundingClientRect();
            return Math.round(ra.top - rb.top) || ra.left - rb.left;
          })
          .forEach((element, index) => {
            element.style.setProperty('--ps-delay', `${Math.min(index, 8) * 90}ms`);
            element.classList.add('is-in');
            revealObserver.unobserve(element);
          });
      },
      { threshold: 0.1 }
    );

    root.querySelectorAll(REVEAL_SELECTOR).forEach((element) => {
      if (element.closest('.ps') && !element.classList.contains('is-in')) revealObserver.observe(element);
    });
  };

  const initScrollEffects = () => {
    const header = document.querySelector('.ps-header-wrapper');
    const heroMedia = document.querySelector('.ps-hero .ps-hero__media');
    const parallax = heroMedia && document.documentElement.classList.contains('ps-motion');
    const desktop = window.matchMedia('(min-width: 750px)');
    let ticking = false;

    const update = () => {
      ticking = false;
      const y = window.scrollY;
      header?.classList.toggle('is-scrolled', y > 8);
      if (parallax) {
        const height = heroMedia.offsetHeight;
        heroMedia.style.transform = desktop.matches && y < height ? `translate3d(0, ${(y * 0.25).toFixed(1)}px, 0)` : '';
      }
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  };

  const initLang = (root = document) => {
    root.querySelectorAll('.ps-lang__btn').forEach((button) => {
      if (button.dataset.psBound) return;
      button.dataset.psBound = 'true';
      button.addEventListener('click', () => {
        const track = button.closest('.ps-lang__track');
        track.style.setProperty('--ps-lang-index', button.style.getPropertyValue('--ps-lang-i'));
        track.classList.remove('is-unset');
        track.querySelectorAll('.ps-lang__btn').forEach((other) => {
          if (other === button) other.setAttribute('aria-current', 'true');
          else other.removeAttribute('aria-current');
        });
      });
    });
  };

  const syncContactTopics = (select) => {
    select.closest('.ps-contact')?.querySelectorAll('[data-ps-subject]').forEach((topic) => {
      const active = topic.dataset.psSubject === select.value;
      topic.classList.toggle('is-active', active);
      topic.setAttribute('aria-pressed', String(active));
    });
  };

  document.addEventListener('click', (event) => {
    const topic = event.target.closest('[data-ps-subject]');
    if (!topic) return;
    const select = document.getElementById(topic.getAttribute('aria-controls'));
    if (!select) return;
    select.value = topic.dataset.psSubject;
    syncContactTopics(select);
    const card = select.closest('.ps-contact__card');
    const message = select.form?.querySelector('textarea');
    card?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
    message?.focus({ preventScroll: true });
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('.ps-contact select')) syncContactTopics(event.target);
  });

  // A theme preview opened on the .myshopify.com domain only stays on the previewed theme while `_fd=0` and
  // `preview_theme_id` are in the URL; without them Shopify redirects to the primary domain and its live theme.
  const initPreviewLinks = () => {
    const theme = window.Shopify?.theme;
    if (!theme || theme.role === 'main' || window.Shopify.designMode) return;
    if (!location.hostname.endsWith('.myshopify.com')) return;
    const params = { _fd: '0', preview_theme_id: String(theme.id) };

    const keepPreview = (event) => {
      const link = event.target.closest('a[href]');
      if (!link || link.target === '_blank') return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.hash && url.pathname === location.pathname) return;
      Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
      link.href = url.toString();
    };
    document.addEventListener('click', keepPreview, true);
    document.addEventListener('auxclick', keepPreview, true);

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if ((form.method || 'get').toLowerCase() !== 'get') return;
        if (new URL(form.action, location.href).origin !== location.origin) return;
        Object.entries(params).forEach(([key, value]) => {
          if (form.querySelector(`input[name="${key}"]`)) return;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.append(input);
        });
      },
      true
    );
  };

  const initRecs = (root = document) => {
    root.querySelectorAll('[data-ps-recs]').forEach(async (section) => {
      if (section.dataset.psBound) return;
      section.dataset.psBound = 'true';
      const track = section.querySelector('[data-ps-recs-track]');
      const prev = section.querySelector('[data-ps-recs-prev]');
      const next = section.querySelector('[data-ps-recs-next]');

      const updateArrows = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
        section.classList.toggle('is-static', max <= 0);
      };
      const page = (direction) => {
        const card = track.querySelector('li');
        const step = card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth;
        const visible = Math.max(1, Math.floor((track.clientWidth + 1) / step));
        track.scrollBy({ left: direction * step * visible, behavior: 'smooth' });
      };
      prev.addEventListener('click', () => page(-1));
      next.addEventListener('click', () => page(1));
      track.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', updateArrows);
      updateArrows();

      const ids = section.dataset.products.split(',').filter(Boolean);
      if (!ids.length) return;
      const exclude = section.dataset.cart;
      const limit = Number(section.dataset.limit) || 10;
      const lists = await Promise.all(
        ids.map((id) =>
          fetch(`${section.dataset.url}&product_id=${id}`)
            .then((response) => (response.ok ? response.text() : ''))
            .then((html) => [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-ps-recs-results] > li')])
            .catch(() => [])
        )
      );
      const seen = new Set();
      const picked = [];
      for (let rank = 0; picked.length < limit && lists.some((list) => list[rank]); rank += 1) {
        lists.forEach((list) => {
          const item = list[rank];
          if (!item || picked.length >= limit) return;
          const handle = item.dataset.handle;
          if (seen.has(handle) || exclude.includes(`|${handle}|`)) return;
          seen.add(handle);
          picked.push(item);
        });
      }
      if (picked.length < 4) return;
      track.replaceChildren(...picked);
      track.scrollLeft = 0;
      updateArrows();
    });
  };

  const initHeroVideo = (root = document) => {
    root.querySelectorAll('[data-ps-hero-video]').forEach((video) => {
      if (video.dataset.psBound) return;
      video.dataset.psBound = 'true';
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const play = () => {
        if (reduced.matches || document.hidden) return;
        video.play().catch(() => {});
      };
      if (reduced.matches) {
        video.removeAttribute('autoplay');
        video.pause();
        return;
      }
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => (entry.isIntersecting ? play() : video.pause())).observe(video);
      }
      document.addEventListener('visibilitychange', () => (document.hidden ? video.pause() : play()));
      play();
    });
  };

  const initHeroSlides = (root = document) => {
    root.querySelectorAll('[data-ps-slides]').forEach((media) => {
      if (media.dataset.psBound) return;
      const slides = [...media.querySelectorAll('.ps-hero__slide')];
      if (slides.length < 2) return;
      media.dataset.psBound = 'true';
      const delay = Math.max(Number(media.dataset.psInterval) || 20, 4) * 1000;
      let index = slides.findIndex((slide) => slide.classList.contains('is-active'));
      let timer;
      let cleanup;

      const show = (next) => {
        const current = slides[index];
        clearTimeout(cleanup);
        slides.forEach((slide) => slide.classList.remove('is-prev'));
        current.classList.remove('is-active');
        current.classList.add('is-prev');
        current.setAttribute('aria-hidden', 'true');
        slides[next].classList.add('is-active');
        slides[next].removeAttribute('aria-hidden');
        index = next;
        cleanup = setTimeout(() => current.classList.remove('is-prev'), 2000);
      };

      const start = () => {
        clearInterval(timer);
        timer = setInterval(() => show((index + 1) % slides.length), delay);
      };

      slides.forEach((slide) => slide.querySelector('img')?.setAttribute('loading', 'eager'));
      document.addEventListener('visibilitychange', () => (document.hidden ? clearInterval(timer) : start()));
      start();
    });
  };

  initPreviewLinks();
  initQuickAdd();
  initRecs();
  initHeroVideo();
  initHeroSlides();
  initDrawers();
  initLang();
  initReveal();
  initScrollEffects();
  window.psMotionReady = true;

  document.addEventListener('shopify:section:load', (event) => {
    initDrawers(event.target);
    initHeroSlides(event.target);
    initHeroVideo(event.target);
    initRecs(event.target);
    initLang(event.target);
    initReveal(event.target);
  });
})();
